import {
  fromDate,
  getLocalTimeZone,
  parseAbsolute,
  toCalendarDate,
  type ZonedDateTime,
} from "@internationalized/date";
import dayjs, { type UnitType } from "dayjs";
import {
  ClockIcon,
  CopyIcon,
  EyeIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
  RefreshCcwDotIcon,
  RefrigeratorIcon,
  TestTube2Icon,
  TrashIcon,
} from "lucide-react";
import React, {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useState,
} from "react";
import type { Key } from "react-aria";
import { Focusable } from "react-aria-components";
import { useMedia } from "react-use";
import { toast } from "sonner";

import {
  createSampleSchema,
  updateSampleSchema,
} from "@blis/shared/schemas/sample";
import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { RoleCondition } from "@/components/RoleCondition";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DataList,
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateHeading,
  EmptyStateIcon,
} from "@/components/ui/empty-state";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import {
  Menu,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { Modal } from "@/components/ui/modal";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { useMediaQuery } from "@/components/ui/utils";
import { useConfig } from "@/lib/config";
import { usePrintLabel } from "@/lib/labels";
import type { RouterOutputs, TAccessionDetail, TSample } from "@/lib/types";
import { fixLocalDate, format, formatDecimalDisplay } from "@/lib/utils";
import { mutationResponse } from "@/utils/trpc";
import { getReceiveTimezone } from "../../lib/receive-timezone";
import {
  getSampleTempDefaults,
  sampleTempFormToUpdatePayload,
} from "../../lib/sample-temp-form";
import { useTimeZoneDisplay } from "../../store";
import { useAccession } from "../AccessionProvider";
import {
  SampleFormCustom,
  type SampleFormValues,
} from "../form-new/components/SampleFormCustom";
import { ReceivedAtDisplay } from "../ReceivedAtDisplay";
import { DetailSection } from "./Section";
import { useDialogProps, useSetDialogState } from "./state";

type SampleContextValue = {
  sample: TSample | null;
  setSample: (sample: TSample | null) => void;
};
const SampleContext = React.createContext<SampleContextValue>(
  {} as SampleContextValue,
);

export function SamplesSection() {
  const [sample, setSample] = React.useState<TSample | null>(null);
  const setAddDialogOpen = useSetDialogState("addSample");
  const { accession } = useAccession();

  const { data, isLoading } = trpc.accession.samples.useQuery(accession.id);
  const samples = data || [];

  return (
    <SampleContext.Provider value={{ sample, setSample }}>
      <DetailSection
        title="Samples"
        actions={
          <div className="flex items-center gap-2">
            <TimeZoneDisplayMenu>
              <TooltipTrigger>
                <Button size="icon-sm" variant="ghost">
                  <ClockIcon />
                </Button>
                <Tooltip>Time display</Tooltip>
              </TooltipTrigger>
            </TimeZoneDisplayMenu>
            <RoleCondition
              roles={[
                "blis_lab_manager",
                "blis_lab_tech",
                "blis_lab_assistant",
              ]}
            >
              <TooltipTrigger>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onPress={() => setAddDialogOpen(true)}
                >
                  <PlusIcon />
                </Button>
                <Tooltip>Add new sample</Tooltip>
              </TooltipTrigger>
            </RoleCondition>
          </div>
        }
      >
        <SampleGrid samples={samples} isLoading={isLoading} />
      </DetailSection>
    </SampleContext.Provider>
  );
}

type OffsetInfo = { units: string; offset: number; label: string };

function getOffset(
  from: Date | string,
  to?: Date | string | null,
  unit: UnitType = "minutes",
) {
  return dayjs(to).diff(from, unit, true);
}

export function getOffsetInfo(
  from: Date | string,
  to?: Date | string,
): OffsetInfo {
  let units: UnitType = "minutes";
  let offset = getOffset(from, to, units);
  const symbol = offset < 0 ? "-" : "+";
  if (offset === 0) {
    return { label: "+0", offset: 0, units: "zero" };
  }
  if (Math.abs(offset) > 60) {
    units = "hours";
    offset = getOffset(from, to, units);
  }
  if (Math.abs(offset) < 1) {
    units = "seconds";
  }
  offset = getOffset(from, to, units);

  return { label: `${symbol}${offset.toFixed(1)}${units[0]}`, offset, units };
}

export function OffsetBadge({
  info,
  verb,
  icon,
  ...props
}: {
  info: OffsetInfo;
  verb: string;
  icon: ReactNode;
  className?: string;
} & Omit<ComponentProps<typeof Badge>, "children">) {
  const difference =
    info.offset <= 0
      ? "at time of"
      : `${info.offset.toFixed(2)} ${info.units} after`;
  return (
    <TooltipTrigger>
      <Focusable>
        <Badge {...props} size="sm" className="gap-1" role="button">
          {icon}
          {info.label}
        </Badge>
      </Focusable>
      <Tooltip>
        {verb} {difference} collection
      </Tooltip>
    </TooltipTrigger>
  );
}

function ExpiredBadge({ sample }: { sample: TSample }) {
  const expirationOffset = sample.expiration
    ? getOffset(dayjs().toDate(), fixLocalDate(sample.expiration), "minute")
    : null;
  const isExpired = expirationOffset !== null && expirationOffset <= 0;
  const isWide = useMedia("(min-width: 900px)");

  const expiredBadge = isWide ? (
    <Badge color="red" size="sm">
      Expired
    </Badge>
  ) : (
    <Badge color="red" size="sm">
      !
    </Badge>
  );

  const almostExpiredBadge = isWide ? (
    <Badge color="orange">
      Expires {dayjs(fixLocalDate(sample.expiration)).fromNow()}
    </Badge>
  ) : (
    <TooltipTrigger>
      <Focusable>
        <Badge color="orange" role="button">
          !
        </Badge>
      </Focusable>
      <Tooltip>
        Expires {dayjs(fixLocalDate(sample.expiration)).fromNow()}
      </Tooltip>
    </TooltipTrigger>
  );

  return isExpired
    ? expiredBadge
    : expirationOffset !== null && expirationOffset <= 43200
      ? almostExpiredBadge
      : null;
}

function SampleMenu({ sample }: { sample: TSample }) {
  const { accession } = useAccession();
  const { printSpecimenLabels } = usePrintLabel();
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [, setRemoveOpen] = useState(false);
  const [, setReceiveOpen] = useState(false);
  const [, setUnreceiveOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const onAction = useCallback(
    (action: Key) => {
      switch (action) {
        case "viewDetails":
          setDetailOpen(true);
          break;
        case "editDetails":
          setEditOpen(true);
          break;
        case "removeFromAccession":
          setRemoveOpen(true);
          break;
        case "delete":
          setDeleteOpen(true);
          break;
        case "printLabel":
          printSpecimenLabels(accession, [sample]);
          break;
        case "receive":
          setReceiveOpen(true);
          break;
        case "unreceive":
          setUnreceiveOpen(true);
          break;
        case "duplicate":
          setDuplicateOpen(true);
          break;
      }
    },
    [accession, sample, printSpecimenLabels],
  );
  return (
    <>
      <MenuTrigger>
        <Button size="icon-sm" variant="ghost">
          <MoreHorizontalIcon />
        </Button>
        <Menu onAction={onAction}>
          <MenuItem id="viewDetails">
            <EyeIcon />
            View details
          </MenuItem>
          <RoleCondition
            roles={["blis_lab_manager", "blis_lab_tech", "blis_lab_assistant"]}
          >
            {/* {sample.received && (
              <MenuItem id="unreceive">
                <CircleDashedIcon />
                Mark not received
              </MenuItem>
            )}
            {!sample.received && (
              <MenuItem id="receive">
                <CircleArrowDownIcon />
                Receive
              </MenuItem>
            )} */}
            <MenuItem id="editDetails">
              <PencilIcon />
              Edit details
            </MenuItem>
            <MenuItem id="duplicate">
              <CopyIcon />
              Duplicate
            </MenuItem>
            <MenuItem id="printLabel">
              <PrinterIcon />
              Print label
            </MenuItem>
            <MenuSeparator />
            <MenuItem isDanger id="delete">
              <TrashIcon />
              Delete sample
            </MenuItem>
          </RoleCondition>
        </Menu>
      </MenuTrigger>
      <SampleDetailsDialog
        sample={sample}
        isOpen={detailOpen}
        onOpenChange={setDetailOpen}
      />
      <SampleEditDialog
        sample={sample}
        isOpen={editOpen}
        onOpenChange={setEditOpen}
      />
      <SampleDeleteDialog
        sample={sample}
        isOpen={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
      <SampleDuplicateDialog
        sample={sample}
        isOpen={duplicateOpen}
        onOpenChange={setDuplicateOpen}
      />
    </>
  );
}

type TubeWithFields = RouterOutputs["samples"]["tubesWithFields"][number];
type Field = NonNullable<TubeWithFields["fieldSet"]>["fields"][number];
type FieldSpec = NonNullable<Field["fieldSpec"]>;
type SampleFieldBuiltIn = NonNullable<Field["builtIn"]>;

function SampleItemBuiltInItem({
  accession,
  sample,
  builtIn,
}: {
  accession: TAccessionDetail;
  sample: TSample;
  builtIn: SampleFieldBuiltIn;
}) {
  switch (builtIn) {
    case "COLLECTION_DATETIME":
      return (
        <DataListItem>
          <DataListLabel>Collection</DataListLabel>
          <DataListValue>
            {format(sample.collection, {
              time: true,
              showTz: true,
              zone: sample.collectionTimezone,
            })}
          </DataListValue>
        </DataListItem>
      );
    case "REFRIGERATION_DATETIME":
      return (
        <DataListItem>
          <DataListLabel>Refrigerated</DataListLabel>
          <DataListValue>
            {sample.refrigerated
              ? format(sample.refrigerated, {
                  time: true,
                  showTz: true,
                  zone: sample.refrigeratedTimezone,
                })
              : "-"}
          </DataListValue>
        </DataListItem>
      );
    case "CENTRIFUGATION_DATETIME":
      return (
        <DataListItem>
          <DataListLabel>Centrifuged</DataListLabel>
          <DataListValue>
            {sample.centrifuged
              ? format(sample.centrifuged, {
                  time: true,
                  showTz: true,
                  zone: sample.centrifugedTimezone,
                })
              : "-"}
          </DataListValue>
        </DataListItem>
      );
    case "LOT_NUMBER":
      return (
        <DataListItem>
          <DataListLabel>Lot</DataListLabel>
          <DataListValue>{sample.lot || "-"}</DataListValue>
        </DataListItem>
      );
    case "LOT_EXPIRATION_DATE":
      return (
        <DataListItem>
          <DataListLabel>Lot expiration</DataListLabel>
          <DataListValue>
            {sample.expiration
              ? format(sample.expiration, { time: false, showTz: false })
              : "-"}
          </DataListValue>
        </DataListItem>
      );
    case "QUANTITY":
      return (
        <DataListItem>
          <DataListLabel>Quantity</DataListLabel>
          <DataListValue>{sample.quantity || "-"}</DataListValue>
        </DataListItem>
      );

    case "PATIENT_STATUS":
      return (
        <DataListItem>
          <DataListLabel>Status</DataListLabel>
          <DataListValue>{sample.tubeType?.description || "-"}</DataListValue>
        </DataListItem>
      );
    case "TRANSFUSION_STATUS":
      return (
        <DataListItem>
          <DataListLabel>Transfusion</DataListLabel>
          <DataListValue>
            {sample.transfusion?.description || "-"}
          </DataListValue>
        </DataListItem>
      );
    default:
      return null;
  }
}

function SampleFieldSpecItem({
  spec,
  sample,
}: {
  spec: FieldSpec;
  sample: TSample;
}) {
  const sampleField = sample.fields.find((f) => f.fieldSpecId === spec.id);
  switch (spec.type) {
    case "TEXT":
      return (
        <DataListItem>
          <DataListLabel>{spec.name}</DataListLabel>
          <DataListValue>{sampleField?.textValue || "-"}</DataListValue>
        </DataListItem>
      );
    case "TEXTAREA":
      return (
        <DataListItem>
          <DataListLabel>{spec.name}</DataListLabel>
          <DataListValue>{sampleField?.textValue || "-"}</DataListValue>
        </DataListItem>
      );
    case "NUMBER":
      return (
        <DataListItem>
          <DataListLabel>{spec.name}</DataListLabel>
          <DataListValue>
            {sampleField?.numericValue?.toString() || "-"}
          </DataListValue>
        </DataListItem>
      );
    case "BOOLEAN":
      return (
        <DataListItem>
          <DataListLabel>{spec.name}</DataListLabel>
          <DataListValue>
            {sampleField?.booleanValue ? "Yes" : "No"}
          </DataListValue>
        </DataListItem>
      );
    case "DATE":
      return (
        <DataListItem>
          <DataListLabel>{spec.name}</DataListLabel>
          <DataListValue>
            {sampleField?.dateValue
              ? format(sampleField.dateValue, { time: false, showTz: false })
              : "-"}
          </DataListValue>
        </DataListItem>
      );
    case "DATETIME":
      return (
        <DataListItem>
          <DataListLabel>{spec.name}</DataListLabel>
          <DataListValue>
            {sampleField?.dateValue
              ? format(sampleField.dateValue, { time: true, showTz: true })
              : "-"}
          </DataListValue>
        </DataListItem>
      );
    case "SELECT": {
      let value = sampleField?.option?.value;
      if (!value && spec.allowFreeText) {
        value = sampleField?.textValue ?? undefined;
      }
      return (
        <DataListItem>
          <DataListLabel>{spec.name}</DataListLabel>
          <DataListValue>{value || "-"}</DataListValue>
        </DataListItem>
      );
    }
    default:
      return null;
  }
}

export function AddSampleDialog({
  accession,
}: {
  accession: TAccessionDetail;
}) {
  const { open, onOpenChange } = useDialogProps("addSample");

  return (
    <DialogTrigger isOpen={open} onOpenChange={onOpenChange}>
      <Modal>
        <SampleDialogInner accession={accession} onOpenChange={onOpenChange} />
      </Modal>
    </DialogTrigger>
  );
}

function SampleDialogInner({
  accession,
  onChange,
  onOpenChange,
}: {
  accession: TAccessionDetail;
  onChange?: (sample: TSample) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const createSample = trpc.accession.createSample.useMutation(
    mutationResponse({
      successMessage: "Sample created",
      errorTitle: "Error creating sample",
      onSuccess: (data) => {
        onChange?.(data[0]);
        onOpenChange(false);
      },
    }),
  );

  const onSubmit = useCallback(
    (values: SampleFormValues) => {
      const collection = transformZonedDateTime(values.collection);
      const refrigerated = transformZonedDateTime(values.refrigerated);
      const centrifuged = transformZonedDateTime(values.centrifuged);

      const converted = {
        ...values,
        collection: collection.date,
        collectionTimezone: collection.timezone,
        refrigerated: refrigerated.date,
        refrigeratedTimezone: refrigerated.timezone,
        centrifuged: centrifuged.date,
        centrifugedTimezone: centrifuged.timezone,
        expiration: values.expiration?.toDate("UTC") || null,
        quantity: values.quantity || 1,
      };

      const parsed = createSampleSchema.safeParse({
        accessionId: accession.id,
        ...converted,
      });

      if (!parsed.success) {
        console.error(parsed.error);
        return;
      }

      createSample.mutate(parsed.data);
    },
    [accession.id, createSample],
  );

  return (
    <Dialog>
      <DialogHeader>
        <DialogTitle>Add sample</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <SampleFormCustom onSubmit={onSubmit} />
      </DialogBody>
      <DialogFooter>
        <Button
          type="submit"
          form="samples-form"
          isPending={createSample.isPending}
        >
          Save
        </Button>
        <DialogCloseButton>Cancel</DialogCloseButton>
      </DialogFooter>
    </Dialog>
  );
}

function SampleLoadingGrid() {
  return (
    <GridList
      items={Array.from({ length: 10 }, (_, i) => ({ id: i }))}
      aria-label="Samples"
      className="border-0 p-0 divide-y"
    >
      {(sample) => (
        <GridListItem
          className="flex items-center gap-2 justify-between rounded-none h-8"
          textValue={sample.id.toString()}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Skeleton className="size-4 rounded-full" />
            <Skeleton className="w-20 rounded-md" />
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <Skeleton className="w-20 rounded-full" />
            <Skeleton className="size-4 rounded-full" />
          </div>
        </GridListItem>
      )}
    </GridList>
  );
}

function SampleGrid({
  samples,
  isLoading,
}: {
  samples: TSample[];
  isLoading: boolean;
}) {
  const setAddDialogOpen = useSetDialogState("addSample");
  return isLoading ? (
    <SampleLoadingGrid />
  ) : (
    <GridList
      items={samples}
      aria-label="Samples"
      className="border-0 p-0 divide-y overflow-visible mb-4"
      renderEmptyState={() => (
        <EmptyState isLoading={isLoading}>
          <EmptyStateIcon>
            <TestTube2Icon />
          </EmptyStateIcon>
          <EmptyStateHeading>No samples</EmptyStateHeading>
          <EmptyStateDescription>
            No samples have been added to this accession.
          </EmptyStateDescription>
          <RoleCondition
            roles={["blis_lab_manager", "blis_lab_tech", "blis_lab_assistant"]}
          >
            <EmptyStateActions>
              <Button size="sm" onPress={() => setAddDialogOpen(true)}>
                <PlusIcon />
                Add sample
              </Button>
            </EmptyStateActions>
          </RoleCondition>
        </EmptyState>
      )}
    >
      {(sample) =>
        sample.displayId.startsWith("S") ||
        sample.displayId.startsWith("TS") ? (
          <SampleGridItem sample={sample} />
        ) : (
          <LegacySampleGridItem sample={sample} />
        )
      }
    </GridList>
  );
}

function SampleGridItem({ sample }: { sample: TSample }) {
  const isWide = useMedia("(min-width: 900px)");
  const [timeZoneDisplay] = useTimeZoneDisplay();
  const { accession } = useAccession();

  return (
    <GridListItem
      className="h-9 px-3 py-0 rounded-none outline-hidden focus-visible:rounded-md focus-visible:ring-inset ring-ring/70 focus-visible:ring-2 flex items-center gap-2 justify-between hover:bg-accent-muted"
      textValue={sample.displayId}
    >
      <div className="flex items-center gap-2 min-w-0">
        {/* {sample.received ? (
          <CircleArrowDownIcon className="size-4 text-indigo-600" />
        ) : (
          <CircleDashedIcon className="size-4 text-muted-foreground" />
        )} */}
        {sample.displayId.startsWith("S") ||
        sample.displayId.startsWith("TS") ? (
          <Badge size="sm" variant="outline" className="font-mono">
            {sample.displayId}
          </Badge>
        ) : (
          <Badge size="md">{sample.quantity}</Badge>
        )}
        <Text bold size="1" truncate className="flex-1 min-w-0 block">
          {sample.tube.name}
        </Text>
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <SampleDisplayOption sample={sample} />
        <ExpiredBadge sample={sample} />
        {isWide && sample.collection && (
          <>
            {sample.refrigerated && (
              <OffsetBadge
                verb="Refrigerated"
                info={getOffsetInfo(sample.collection, sample.refrigerated)}
                icon={<RefrigeratorIcon className="size-3" />}
                color="blue"
                className="hidden md:inline-flex"
              />
            )}
            {sample.centrifuged && (
              <OffsetBadge
                verb="Centrifuged"
                info={getOffsetInfo(sample.collection, sample.centrifuged)}
                icon={<RefreshCcwDotIcon className="size-3" />}
                color="amber"
                className="hidden md:inline-flex"
              />
            )}
          </>
        )}
        <Text muted size="1" className="flex shrink-0">
          {format(sample.collection, {
            showTz: timeZoneDisplay === "collection",
            zone:
              timeZoneDisplay === "collection"
                ? sample.collectionTimezone
                : undefined,
          })}
        </Text>
        <SampleMenu sample={sample} />
      </div>
    </GridListItem>
  );
}

function SampleDisplayOption({ sample }: { sample: TSample }) {
  const fieldsToDisplay = sample.fields.filter(
    (field) =>
      (field.fieldSpec?.displayOptions as Record<string, boolean>)?.accession,
  );
  return fieldsToDisplay.map((field) => (
    <Badge size="sm" variant="outline" key={field.id}>
      {field.option?.value ?? field.textValue ?? field.numericValue?.toString()}
    </Badge>
  ));
}

function LegacySampleGridItem({ sample }: { sample: TSample }) {
  const isWide = useMedia("(min-width: 900px)");

  return (
    <GridListItem
      className="h-9 px-3 py-0 rounded-none outline-hidden focus-visible:rounded-md focus-visible:ring-inset ring-ring/70 focus-visible:ring-2 flex items-center gap-2 justify-between hover:bg-accent-muted"
      textValue={sample.displayId}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Badge size="md">{sample.quantity}</Badge>
        <Text bold size="1" truncate className="flex-1 min-w-0 block">
          {sample.tube.name}
        </Text>
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <ExpiredBadge sample={sample} />
        {isWide && sample.collection && (
          <>
            {sample.refrigerated && (
              <OffsetBadge
                verb="Refrigerated"
                info={getOffsetInfo(sample.collection, sample.refrigerated)}
                icon={<RefrigeratorIcon className="size-3" />}
                color="blue"
                className="hidden md:inline-flex"
              />
            )}
            {sample.centrifuged && (
              <OffsetBadge
                verb="Centrifuged"
                info={getOffsetInfo(sample.collection, sample.centrifuged)}
                icon={<RefreshCcwDotIcon className="size-3" />}
                color="amber"
                className="hidden md:inline-flex"
              />
            )}
          </>
        )}
        <Text muted size="1" className="flex shrink-0">
          {format(sample.collection)}
        </Text>
        <SampleMenu sample={sample} />
      </div>
    </GridListItem>
  );
}

function SampleDetailsDialog({
  sample,
  isOpen,
  onOpenChange,
}: {
  sample: TSample;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { accession } = useAccession();
  const { data: tubes } = trpc.samples.tubesWithFields.useQuery();
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const tube = tubes?.find((t) => t.id === sample?.tubeId);
  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Sample details - {sample.displayId}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DataList orientation={isMobile ? "vertical" : "horizontal"}>
            <DataListItem>
              <DataListLabel>Sample ID</DataListLabel>
              <DataListValue>
                <Badge size="sm" variant="outline" className="font-mono">
                  {sample.displayId}
                </Badge>
              </DataListValue>
            </DataListItem>

            <DataListItem>
              <DataListLabel>Received</DataListLabel>
              <DataListValue>
                {accession.received ? (
                  <div className="flex items-center gap-2">
                    <ReceivedAtDisplay
                      receivedAt={accession.receivedAt}
                      receiveTimezone={getReceiveTimezone(accession)}
                    />
                    {sample.receivedBy?.username && (
                      <span className="text-muted-foreground">
                        by {sample.receivedBy.username}
                      </span>
                    )}
                  </div>
                ) : (
                  "-"
                )}
              </DataListValue>
            </DataListItem>

            <DataListItem>
              <DataListLabel>Received temp</DataListLabel>
              <DataListValue>
                {sample.temp != null || sample.tempType
                  ? sample.temp != null
                    ? `${formatDecimalDisplay(sample.temp)} °${sample.tempUnits}`
                    : sample.tempType
                  : "-"}
              </DataListValue>
            </DataListItem>

            {/* <DataListItem>
              <DataListLabel>Packed</DataListLabel>
              <DataListValue>
                {sample.packedAt
                  ? format(sample.packedAt, { showTz: true })
                  : "-"}
              </DataListValue>
            </DataListItem> */}

            <Separator />

            <DataListItem>
              <DataListLabel>Tube Type</DataListLabel>
              <DataListValue>{tube?.name || "Unknown"}</DataListValue>
            </DataListItem>

            {tube?.fieldSet?.fields
              .filter((f) => f.fieldSpec || f.builtIn !== "QUANTITY")
              .map((field) =>
                field.builtIn ? (
                  <SampleItemBuiltInItem
                    key={field.id}
                    accession={accession}
                    sample={sample}
                    builtIn={field.builtIn}
                  />
                ) : field.fieldSpec ? (
                  <SampleFieldSpecItem
                    key={field.id}
                    spec={field.fieldSpec}
                    sample={sample}
                  />
                ) : null,
              )}
          </DataList>
        </DialogBody>
        <DialogFooter />
      </Dialog>
    </Modal>
  );
}

function SampleEditDialog({
  sample,
  isOpen,
  onOpenChange,
}: {
  sample: TSample;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { accession } = useAccession();
  const config = useConfig();
  const updateSample = trpc.accession.updateSample.useMutation(
    mutationResponse({
      successMessage: "Sample updated",
      errorTitle: "Error updating sample",
      onSuccess: () => {
        onOpenChange(false);
      },
    }),
  );
  const formattedSample = React.useMemo(() => {
    if (!sample) return undefined;
    const localTz = getLocalTimeZone();
    return {
      tubeId: sample.tubeId,
      collection: sample.collection
        ? fromDate(sample.collection, sample.collectionTimezone || localTz)
        : undefined,
      refrigerated: sample.refrigerated
        ? fromDate(sample.refrigerated, sample.refrigeratedTimezone || localTz)
        : undefined,
      centrifuged: sample.centrifuged
        ? fromDate(sample.centrifuged, sample.centrifugedTimezone || localTz)
        : undefined,
      expiration: sample.expiration
        ? toCalendarDate(fromDate(sample.expiration, "UTC"))
        : undefined,
      quantity: sample.quantity,
      lot: sample.lot,
      transfusionId: sample.transfusionId,
      typeId: sample.tubeType?.value,
      fields: sample.fields.map((values) => ({
        ...values,
        dateValue: values.dateValue
          ? toCalendarDate(fromDate(values.dateValue, "UTC"))
          : null,
        numericValue: values.numericValue?.toNumber() || null,
      })),
      ...(accession.received
        ? getSampleTempDefaults(sample, accession.packedAt)
        : {}),
    };
  }, [sample, accession.packedAt, accession.received]);

  const onSubmit = useCallback(
    (values: SampleFormValues) => {
      const collection = transformZonedDateTime(values.collection);
      const refrigerated = transformZonedDateTime(values.refrigerated);
      const centrifuged = transformZonedDateTime(values.centrifuged);

      const parsed = updateSampleSchema.safeParse({
        ...values,
        ...(accession.received
          ? sampleTempFormToUpdatePayload(values, sample, accession.packedAt)
          : {}),
        id: sample.id,
        accessionId: accession.id,
        collection: collection.date,
        collectionTimezone: collection.timezone,
        refrigerated: refrigerated.date,
        refrigeratedTimezone: refrigerated.timezone,
        centrifuged: centrifuged.date,
        centrifugedTimezone: centrifuged.timezone,
        expiration: values.expiration?.toDate("UTC") || null,
        quantity: 1,
      });
      if (!parsed.success) {
        console.error(parsed.error);
        return;
      }
      updateSample.mutate(parsed.data);
    },
    [
      sample,
      updateSample,
      accession.id,
      accession.packedAt,
      accession.received,
    ],
  );

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Edit sample</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <SampleFormCustom
            onSubmit={onSubmit}
            sample={formattedSample}
            hideTimeZone={!config.enableSubjectSampleMigration}
            hiddenFields={
              config.enableSubjectSampleMigration ? ["QUANTITY"] : undefined
            }
            showReceivedTemp={accession.received}
            packedAt={accession.packedAt}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            type="submit"
            form="samples-form"
            isPending={updateSample.isPending}
          >
            Save
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

function SampleDeleteDialog({
  sample,
  isOpen,
  onOpenChange,
}: {
  sample: TSample;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const deleteSample = trpc.accession.deleteSample.useMutation(
    mutationResponse({
      successMessage: "Sample deleted",
      errorTitle: "Error deleting sample",
      onSuccess: () => {
        onOpenChange(false);
      },
    }),
  );
  return (
    <ConfirmPrompt
      danger
      open={isOpen}
      onOpenChange={onOpenChange}
      onConfirm={() => deleteSample.mutate(sample.id)}
      loading={deleteSample.isPending}
      title="Delete sample"
      message="Are you sure you want to delete this sample?"
      warningText="The sample will be deleted from the system. If you want to remove it from the accession, use the 'Remove from accession' action instead."
    />
  );
}

// function SampleUnreceiveDialog({
//   sample,
//   isOpen,
//   onOpenChange,
// }: {
//   sample: TSample;
//   isOpen: boolean;
//   onOpenChange: (open: boolean) => void;
// }) {
//   const unreceive = trpc.samples.unreceive.useMutation(
//     mutationResponse({
//       successMessage: "Sample unreceived",
//       errorTitle: "Error unreceiving sample",
//       onSuccess() {
//         onOpenChange(false);
//       },
//     }),
//   );
//   return (
//     <ConfirmPrompt
//       danger
//       open={isOpen}
//       onOpenChange={onOpenChange}
//       onConfirm={() => unreceive.mutate({ sampleId: sample.id })}
//       loading={unreceive.isPending}
//       title="Revert sample receipt"
//       message="Are you sure you want to revert the receipt of this sample?"
//     />
//   );
// }

function SampleDuplicateDialog({
  sample,
  isOpen,
  onOpenChange,
}: {
  sample: TSample;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const duplicate = trpc.samples.duplicate.useMutation(
    mutationResponse({
      successMessage: "Sample duplicated",
      errorTitle: "Error duplicating sample",
      onSuccess(data) {
        onOpenChange(false);
        toast.success(`Sample duplicated to ${data.displayId}`);
      },
    }),
  );
  return (
    <ConfirmPrompt
      open={isOpen}
      onOpenChange={onOpenChange}
      onConfirm={() => duplicate.mutate({ sampleId: sample.id })}
      loading={duplicate.isPending}
      title="Duplicate sample"
      message="Are you sure you want to duplicate this sample?"
    />
  );
}

function TimeZoneDisplayMenu({ children }: { children: ReactNode }) {
  const [timeZoneDisplay, setTimeZoneDisplay] = useTimeZoneDisplay();
  return (
    <MenuTrigger>
      {children}
      <Menu
        selectionMode="single"
        selectedKeys={[timeZoneDisplay]}
        onSelectionChange={(keys) => {
          if (keys !== "all" && keys.size === 1) {
            const key = Array.from(keys).pop();
            if (key) {
              setTimeZoneDisplay(key as "local" | "collection");
            }
          }
        }}
      >
        <MenuItem id="local">Local time zone</MenuItem>
        <MenuItem id="collection">Collection time zone</MenuItem>
      </Menu>
    </MenuTrigger>
  );
}

export function transformZonedDateTime(
  zoned: ZonedDateTime | null | undefined,
) {
  return {
    date: zoned?.toAbsoluteString() ?? null,
    timezone: zoned?.timeZone ?? null,
  };
}

export function parseZonedDateTime(
  date: string | null | undefined,
  timezone: string | null | undefined,
) {
  if (!date || !timezone) return null;
  return parseAbsolute(date, timezone);
}
